import React, { useEffect, useRef, useState } from 'react';
import {
  Table, Thead, Tbody, Tr, Th, Td, Button, Modal, ModalOverlay, ModalContent,
  ModalHeader, ModalBody, ModalFooter, useDisclosure, Badge, Flex, Text, Box,
  HStack, Select, Input, useToast,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay,
} from '@chakra-ui/react';
import { AddIcon, DownloadIcon } from '@chakra-ui/icons';
import * as XLSX from 'xlsx';
import deportesApi, { Articulo } from '../../../api/deportesApi';
import FormularioArticulo, { ESTADOS_FISICOS } from './FormularioArticulo';
import ModalMovimientos from './ModalMovimientos';
import { useCatalogos, Paginacion, fmtFecha, fmtMoneda, ROJO, ROJO_HOVER } from '../_shared';

const TablaInventario: React.FC = () => {
  const toast = useToast();
  const cancelRef = useRef<any>(null);
  const { categoriasInventario } = useCatalogos();

  const { isOpen: isVer, onOpen: onVer, onClose: onCloseVer } = useDisclosure();
  const { isOpen: isEditar, onOpen: onEditar, onClose: onCloseEditar } = useDisclosure();
  const { isOpen: isNuevo, onOpen: onNuevo, onClose: onCloseNuevo } = useDisclosure();
  const { isOpen: isMov, onOpen: onMov, onClose: onCloseMov } = useDisclosure();
  const { isOpen: isEliminar, onOpen: onEliminar, onClose: onCloseEliminar } = useDisclosure();

  const [sel, setSel] = useState<Articulo | null>(null);
  const [data, setData] = useState<Articulo[]>([]);
  const [cargando, setCargando] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [fNombre, setFNombre] = useState('');
  const [fCategoria, setFCategoria] = useState('');
  const [fEstado, setFEstado] = useState('');
  const [fUbicacion, setFUbicacion] = useState('');

  const fetchData = async () => {
    setCargando(true);
    try {
      const res = await deportesApi.listarArticulos({
        nombre: fNombre, categoriaId: fCategoria, estadoFisico: fEstado, ubicacion: fUbicacion,
      });
      setData(res || []);
    } catch { setData([]); } finally { setCargando(false); }
  };

  useEffect(() => {
    const t = setTimeout(() => { setCurrentPage(1); fetchData(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fNombre, fCategoria, fEstado, fUbicacion]);

  const totalPages = Math.ceil(data.length / itemsPerPage);
  const indexUltimo = currentPage * itemsPerPage;
  const indexPrimero = indexUltimo - itemsPerPage;
  const itemsPagina = data.slice(indexPrimero, indexUltimo);

  const abrir = async (item: Articulo, accion: 'ver' | 'editar' | 'mov') => {
    try { setSel(await deportesApi.obtenerArticulo(item.id)); }
    catch { setSel(item); }
    if (accion === 'ver') onVer();
    else if (accion === 'editar') onEditar();
    else onMov();
  };

  const handleGuardado = () => { onCloseNuevo(); onCloseEditar(); fetchData(); };

  const handleEliminar = async () => {
    if (!sel) return;
    setProcesando(true);
    try {
      await deportesApi.eliminarArticulo(sel.id);
      toast({ title: 'Artículo eliminado', status: 'success', duration: 3000 });
      onCloseEliminar(); fetchData();
    } catch (e: any) { toast({ title: 'Error', description: e?.response?.data?.message, status: 'error', duration: 4000 }); }
    finally { setProcesando(false); }
  };

  const exportExcel = () => {
    const filas = data.map((a) => ({
      'No. Inventario': a.numeroInventario, Nombre: a.nombre, Categoría: a.categoria?.nombre || '',
      Marca: a.marca || '', Modelo: a.modelo || '', Cantidad: a.cantidad ?? 0, Disponible: a.cantidadDisponible ?? 0,
      'Estado físico': a.estadoFisico || '', Ubicación: a.ubicacion || '', Costo: a.costo ?? '',
      Adquisición: fmtFecha(a.fechaAdquisicion),
    }));
    const ws = XLSX.utils.json_to_sheet(filas);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Inventario');
    XLSX.writeFile(wb, `Inventario_Deportes_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const [generandoPdf, setGenerandoPdf] = useState(false);
  const reportePdf = async () => {
    setGenerandoPdf(true);
    try {
      const blob = await deportesApi.reporteInventarioPdf({
        nombre: fNombre, categoriaId: fCategoria, estadoFisico: fEstado, ubicacion: fUbicacion,
      });
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (e: any) {
      toast({ title: 'Error', description: e?.response?.data?.message || 'No se pudo generar el PDF.', status: 'error', duration: 4000 });
    } finally { setGenerandoPdf(false); }
  };

  return (
    <Box>
      <Flex mb={4} justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <HStack spacing={2} flexWrap="wrap">
          <Input placeholder="Buscar por nombre..." value={fNombre} onChange={(e) => setFNombre(e.target.value)} size="sm" w={{ base: 'full', md: '200px' }} />
          <Select placeholder="Categoría" value={fCategoria} onChange={(e) => setFCategoria(e.target.value)} size="sm" w="160px">
            {categoriasInventario.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </Select>
          <Select placeholder="Estado físico" value={fEstado} onChange={(e) => setFEstado(e.target.value)} size="sm" w="150px">
            {ESTADOS_FISICOS.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
          <Input placeholder="Ubicación" value={fUbicacion} onChange={(e) => setFUbicacion(e.target.value)} size="sm" w="140px" />
        </HStack>
        <HStack spacing={2}>
          <Button size="sm" leftIcon={<DownloadIcon />} colorScheme="green" variant="outline" onClick={exportExcel}>Excel</Button>
          <Button size="sm" leftIcon={<DownloadIcon />} bg={ROJO} color="white" _hover={{ bg: ROJO_HOVER }} onClick={reportePdf} isLoading={generandoPdf} loadingText="Generando...">Reporte PDF</Button>
          <Button size="sm" leftIcon={<AddIcon />} bg={ROJO} color="white" _hover={{ bg: ROJO_HOVER }} onClick={onNuevo}>Nuevo artículo</Button>
        </HStack>
      </Flex>

      <Box overflowX="auto" borderRadius="md" boxShadow="md" bg="white">
        <Table variant="simple" size="sm">
          <Thead bg={ROJO}>
            <Tr>
              <Th color="white" fontSize="xs">No. Inv.</Th>
              <Th color="white" fontSize="xs">Artículo</Th>
              <Th color="white" fontSize="xs">Categoría</Th>
              <Th color="white" fontSize="xs" isNumeric>Total</Th>
              <Th color="white" fontSize="xs" isNumeric>Disp.</Th>
              <Th color="white" fontSize="xs">Estado</Th>
              <Th color="white" fontSize="xs">Ubicación</Th>
              <Th color="white" fontSize="xs">Costo</Th>
              <Th color="white" fontSize="xs">Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {cargando ? (
              <Tr><Td colSpan={9} textAlign="center" py={8}><Text color="gray.500">Cargando...</Text></Td></Tr>
            ) : itemsPagina.length === 0 ? (
              <Tr><Td colSpan={9} textAlign="center" py={8}><Text color="gray.400">No se encontraron artículos</Text></Td></Tr>
            ) : itemsPagina.map((a, idx) => (
              <Tr key={a.id} bg={idx % 2 === 0 ? 'white' : 'gray.50'} _hover={{ bg: 'red.50' }}>
                <Td fontSize="xs">{a.numeroInventario}</Td>
                <Td maxW="200px"><Text noOfLines={1} fontSize="sm" fontWeight="medium">{a.nombre}</Text><Text fontSize="2xs" color="gray.400">{[a.marca, a.modelo].filter(Boolean).join(' ')}</Text></Td>
                <Td>{a.categoria?.nombre || '—'}</Td>
                <Td isNumeric>{a.cantidad ?? 0}</Td>
                <Td isNumeric><Text color={(a.cantidadDisponible ?? 0) === 0 ? 'red.500' : 'green.600'} fontWeight="bold">{a.cantidadDisponible ?? 0}</Text></Td>
                <Td><Badge>{a.estadoFisico}</Badge></Td>
                <Td fontSize="xs">{a.ubicacion || '—'}</Td>
                <Td fontSize="xs">{fmtMoneda(a.costo)}</Td>
                <Td>
                  <HStack spacing={1} flexWrap="wrap">
                    <Button size="xs" colorScheme="blue" variant="outline" onClick={() => abrir(a, 'ver')}>Ver</Button>
                    <Button size="xs" colorScheme="orange" variant="outline" onClick={() => abrir(a, 'editar')}>Editar</Button>
                    <Button size="xs" bg={ROJO} color="white" _hover={{ bg: ROJO_HOVER }} onClick={() => abrir(a, 'mov')}>Movimientos</Button>
                    <Button size="xs" colorScheme="red" variant="outline" onClick={() => { setSel(a); onEliminar(); }}>Eliminar</Button>
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      <Paginacion currentPage={currentPage} totalPages={totalPages} total={data.length} indexPrimero={indexPrimero} indexUltimo={indexUltimo} onChange={setCurrentPage} />

      <Modal isOpen={isVer} onClose={onCloseVer} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={ROJO}>Detalle del Artículo</ModalHeader>
          <ModalBody py={5}>{sel && <FormularioArticulo modo="view" data={sel} />}</ModalBody>
          <ModalFooter><Button onClick={onCloseVer}>Cerrar</Button></ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isEditar} onClose={onCloseEditar} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={ROJO}>Editar Artículo</ModalHeader>
          <ModalBody py={5}>{sel && <FormularioArticulo modo="edit" data={sel} onGuardado={handleGuardado} onCancelar={onCloseEditar} />}</ModalBody>
        </ModalContent>
      </Modal>

      <Modal isOpen={isNuevo} onClose={onCloseNuevo} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={ROJO}>Nuevo Artículo</ModalHeader>
          <ModalBody py={5}><FormularioArticulo modo="new" onGuardado={handleGuardado} onCancelar={onCloseNuevo} /></ModalBody>
        </ModalContent>
      </Modal>

      <ModalMovimientos isOpen={isMov} onClose={onCloseMov} articulo={sel} onCambio={fetchData} />

      <AlertDialog isOpen={isEliminar} leastDestructiveRef={cancelRef} onClose={onCloseEliminar}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color={ROJO}>Eliminar artículo</AlertDialogHeader>
            <AlertDialogBody>¿Eliminar el artículo <strong>{sel?.nombre}</strong>?</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onCloseEliminar} isDisabled={procesando}>Cancelar</Button>
              <Button colorScheme="red" onClick={handleEliminar} ml={3} isLoading={procesando} loadingText="Eliminando...">Eliminar</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default TablaInventario;
